import React from 'react';
import { Form } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import {
  Drawer,
  Input,
  Select,
  Button,
  Spin,
  message,
  Divider,
  Skeleton,
  Modal,
  Col,
  Row,
  Radio,
  Switch,
  Popconfirm,
} from 'antd';
import 'antd/dist/antd.css';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import { Driver, DrivingCompany } from './types';
import { FETCH_DRIVER_INFO, FETCH_DRIVING_COMPANIES } from './queries';
import { UPDATE_DRIVER } from './mutations';
import { getClient } from '../../apollo';
import {
  ContactsDocument,
  ContactsQuery,
  ContactsQueryVariables,
  DeleteDriverDocument,
  DeleteDriverMutation,
  DeleteDriverMutationVariables,
  GetAllDriversDocument,
  GetAllDriversQuery,
  GetAllDriversQueryVariables,
  LoginMethod,
} from '../../graphql/datamodel.gen';
import AvatarUploader, {
  IFirebaseImage,
} from '../avatarUploader/avatarUploader';
import { getMobileApk } from '../../utils/mobile';
import { sendEmail, validateEmail } from '../../utils/email';
import { validatePhone } from '../../utils/phone';

const { Option } = Select;

interface IUpdateDriverFormProps extends FormComponentProps {
  driver: Driver;
  drivingCompanies: DrivingCompany[];
  onSubmit: any;
  onClickDelete: () => any;
}

class UpdateDriverForm extends React.Component<IUpdateDriverFormProps> {
  state = {
    email: undefined,
    phone: undefined,
    nameEditing: false,
    phoneEditing: false,
    emailEditing: false,
    editionLoading: false,
    type: undefined as 'own' | 'collaborator' | undefined,
    profilePicURI: undefined,
    loginMethod: undefined,
    loginEnabled: undefined,
  };

  constructor(props: IUpdateDriverFormProps) {
    super(props);

    this.state = {
      email: this.props.driver?.email,
      phone: this.props.driver?.phone,
      nameEditing: false,
      phoneEditing: false,
      emailEditing: false,
      editionLoading: false,
      type: this.props.driver?.type,
      profilePicURI: undefined,
      loginMethod: this.props.driver?.loginMethod,
      loginEnabled: this.props.driver?.loginEnabled,
    };
  }

  emailValidator = async (
    rule: any,
    value: any,
    callback: (arg0?: string) => void,
  ) => {
    const invalid = await validateEmail(value, {
      isRequired: { enabled: this.state.loginMethod === 'EMAIL' },
      checkAgainstFirebase: {
        enabled: true,
        message: 'Ya existe un conductor con ese email',
        previousEmail: this.props.driver?.email,
        newEmail: value,
      },
    });

    if (invalid) return callback(invalid);
    return callback();
  };

  phoneValidator = (
    rule: any,
    value: any,
    callback: { (arg0: string): void; (): void },
  ) => {
    const invalid = validatePhone(value, {
      isRequired: {
        enabled: this.state.loginMethod === 'PHONE',
      },
    });
    if (invalid) {
      return callback(invalid);
    }
    return callback();
  };

  handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    this.props.form.validateFields(async (err, values) => {
      if (!err) {
        const {
          name,
          phone,
          email,
          type,
          status,
          drivingCompany: drivingCompanyId,
        } = values;

        this.setState({
          editionLoading: true,
        });
        message.loading({
          content: 'Modificando conductor',
          key: 'updateDriver',
        });

        try {
          const response = await getClient().mutate({
            mutation: UPDATE_DRIVER,
            variables: {
              id: this.props.driver.id || this.props.driver.volcanoDriverId,
              name,
              email,
              phone,
              type,
              status,
              drivingCompanyId,
              disableLogin: !this.state.loginEnabled,
              loginMethod: this.state.loginMethod,
              profilePicURI: this.state.profilePicURI,
            },
          });
          message.success({
            content: 'Conductor modificado!',
            key: 'updateDriver',
          });
          this.setState({
            editionLoading: false,
          });
          this.props.onSubmit(true);
        } catch (err) {
          message.error({
            content: 'Error al modificar conductor!',
            key: 'updateDriver',
          });
          this.setState({
            editionLoading: false,
          });
          this.props.onSubmit(false);
        } finally {
          setTimeout(() => {
            message.destroy();
          }, 2000);
        }
      }
    });
  };

  onClickEditName = () => {
    if (this.state.nameEditing) {
      this.setState({
        nameEditing: false,
      });
    } else {
      this.setState({
        nameEditing: true,
      });
    }
  };

  onClickEditPhone = () => {
    if (this.state.phoneEditing) {
      this.setState({
        phoneEditing: false,
      });
    } else {
      this.setState({
        phoneEditing: true,
      });
    }
  };

  onClickEditEmail = () => {
    if (this.state.emailEditing) {
      this.setState({
        emailEditing: false,
      });
    } else {
      this.setState({
        emailEditing: true,
      });
    }
  };

  computeIconType = (key: string) => {
    if (key === 'name') {
      if (this.state.nameEditing) {
        return 'check';
      }

      return 'edit';
    }

    if (key === 'phone') {
      if (this.state.phoneEditing) {
        return 'check';
      }

      return 'edit';
    }

    return 'edit';
  };

  onTypeChange = (value: any) => {
    this.setState({
      type: value,
    });
  };

  onProfilePicUploadComplete = (image: IFirebaseImage) => {
    this.setState({
      profilePicURI: image.reference,
    });
  };

  onRadioGroupChange = (e) => {
    this.setState(
      {
        loginMethod: e.target.value,
      },
      () => {
        this.props.form.validateFields();
      },
    );
  };

  render() {
    // @ts-ignore
    const { getFieldDecorator } = this.props.form;
    return (
      <Form
        id={'update-driver-form'}
        onSubmit={this.handleSubmit}
        layout="vertical"
      >
        <Row gutter={32}>
          <Col span={12}>
            <Form.Item required={false} label={'Método de inicio de sesión'}>
              {getFieldDecorator('signupMethod', {
                initialValue: this.state.loginMethod,
                rules: [
                  {
                    required: false,
                  },
                ],
              })(
                <Radio.Group
                  onChange={this.onRadioGroupChange}
                  buttonStyle="solid"
                >
                  <Radio.Button value={'EMAIL'}>Email</Radio.Button>
                  <Radio.Button value={'PHONE'}>Teléfono móvil</Radio.Button>
                </Radio.Group>,
              )}
            </Form.Item>

            <Form.Item label={'Acceso a la aplicación móvil'}>
              <Switch
                onClick={(checked) => {
                  this.setState({
                    loginEnabled: checked,
                  });
                }}
                checkedChildren="Sí"
                unCheckedChildren="No"
                defaultChecked={this.props.driver?.loginEnabled}
              />
            </Form.Item>

            <Form.Item colon={false} label={'Nombre'}>
              {getFieldDecorator('name', {
                initialValue: this.props.driver.name,
                rules: [
                  {
                    type: 'string',
                    message: 'El nombre introducido no es válido',
                  },
                  {
                    required: true,
                    message: 'No puedes dejar nombre en blanco',
                  },
                ],
              })(<Input placeholder={this.props.driver.name} />)}
            </Form.Item>
            <Form.Item
              colon={false}
              label={'Email'}
              required={this.state.loginMethod === 'EMAIL'}
            >
              {getFieldDecorator('email', {
                initialValue: this.props.driver.email,
                rules: [
                  {
                    type: 'email',
                    message: 'El email introducido no es válido',
                  },
                  {
                    validator: this.emailValidator,
                  },
                ],
              })(
                <Input
                  onChange={(event) => {
                    const email = event.target.value;
                    this.setState({
                      email,
                    });
                  }}
                  disabled={!this.state.phone}
                  //   addonAfter={
                  //     <Icon
                  //       onClick={this.onClickEditEmail}
                  //       type={this.computeIconType('email')}
                  //     />
                  //   }
                  placeholder={this.props.driver.email}
                />,
              )}
            </Form.Item>
            <Form.Item
              colon={false}
              label={'Teléfono'}
              required={this.state.loginMethod === 'PHONE'}
            >
              {getFieldDecorator('phone', {
                initialValue: this.props.driver.phone,
                rules: [
                  {
                    type: 'string',
                    message: 'El teléfono introducido no es válido',
                  },
                  {
                    required: this.state.loginMethod === 'PHONE',
                    message: 'Por favor introduce un número de teléfono',
                  },
                ],
              })(
                <Input
                  onChange={(event) => {
                    const phone = event.target.value;
                    this.setState({
                      phone,
                    });
                  }}
                  placeholder={this.props.driver.phone}
                />,
              )}
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item colon={false} label={'Estado'}>
              {getFieldDecorator('status', {
                initialValue: this.props.driver.status,
              })(
                <Select>
                  <Option key={'active'} value={'active'}>
                    Activo
                  </Option>
                  <Option key={'inactive'} value={'inactive'}>
                    Inactivo
                  </Option>
                </Select>,
              )}
            </Form.Item>
            {this.state.type === 'collaborator' && (
              <Form.Item colon={false} label={'Empresa colaboradora'}>
                {getFieldDecorator('drivingCompany', {
                  initialValue: this.props.driver.drivingCompany
                    ? this.props.driver.drivingCompany.id
                    : undefined,
                  rules: [
                    {
                      required: true,
                      message: 'Por favor, asigna una empresa',
                    },
                  ],
                })(
                  <Select
                    allowClear
                    showSearch
                    placeholder="Selecciona una empresa colaboradora"
                  >
                    {this.props.drivingCompanies.map((company) => (
                      <Option
                        key={company.id || company.volcanoId}
                        value={company.id || company.volcanoId}
                      >
                        {company.name}
                      </Option>
                    ))}
                  </Select>,
                )}
              </Form.Item>
            )}
            <Form.Item label={'Foto de perfil'}>
              <AvatarUploader
                overrideImageURL={this.props.driver.profilePicURI}
                storageRef={'/drivers/profile_pics/'}
                onUploadComplete={this.onProfilePicUploadComplete}
              />
            </Form.Item>
          </Col>
        </Row>

        {/*<Divider />*/}
        {/*<Form.Item>*/}
        {/*  <a*/}
        {/*    className="login-form-delete"*/}
        {/*    href=""*/}
        {/*    onClick={e => {*/}
        {/*      e.preventDefault();*/}
        {/*      this.props.onClickDelete();*/}
        {/*    }}*/}
        {/*  >*/}
        {/*    Dar de baja del sistema*/}
        {/*  </a>*/}
        {/*</Form.Item>*/}
      </Form>
    );
  }
}

const MyDriverUpdateForm =
  Form.create<IUpdateDriverFormProps>()(UpdateDriverForm);

interface IUpdateDriverDrawerProps {
  driverId?: string;
  visible: boolean;
  onClose: () => void;
  onSubmit: (success?: boolean) => void;
  hideDrawer: (...args: any[]) => void;
}

interface IUpdateDriverDrawerState {
  driver?: Driver;
  drivingCompanies: DrivingCompany[];
}

export default class UpdateDriverDrawer extends React.Component<
  IUpdateDriverDrawerProps,
  IUpdateDriverDrawerState
> {
  state = {
    driver: undefined,
    drivingCompanies: [],
  };

  async componentDidMount() {
    if (this.props.driverId) {
      const response = await getClient().query({
        query: FETCH_DRIVING_COMPANIES,
        fetchPolicy: 'network-only',
      });

      let drivingCompanies = response.data.drivingCompanies || [];

      const responseDriver = await getClient().query({
        query: FETCH_DRIVER_INFO,
        fetchPolicy: 'network-only',
        variables: {
          id: this.props.driverId,
        },
      });

      const { driver } = responseDriver.data;

      this.setState({
        driver,
        drivingCompanies,
      });
    }
  }

  static async getDerivedStateFromProps(
    nextProps: IUpdateDriverDrawerProps,
    _: any,
  ) {
    if (nextProps.driverId) {
      const response = await getClient().query({
        query: FETCH_DRIVING_COMPANIES,
      });

      let drivingCompanies = response.data.drivingCompanies || [];

      const responseDriver = await getClient().query({
        query: FETCH_DRIVER_INFO,
        variables: {
          id: nextProps.driverId,
        },
      });

      const { driver } = responseDriver.data;

      return {
        driver,
        drivingCompanies,
      };
    }

    return null;
  }

  onDelete = async () => {
    try {
      message.loading(
        {
          content: 'Eliminando cuenta..',
          key: 'driver_delete',
        },
        0,
      );

      const response = await getClient().mutate<
        DeleteDriverMutation,
        DeleteDriverMutationVariables
      >({
        mutation: DeleteDriverDocument,
        variables: {
          id: this.props.driverId,
        },
        update: (cache, result, context) => {
          cache.updateQuery<GetAllDriversQuery, GetAllDriversQueryVariables>(
            {
              query: GetAllDriversDocument,
            },
            (data) => {
              return {
                ...data,
                drivers: [
                  ...data.drivers.filter((d) => d.id !== context.variables.id),
                ],
              };
            },
          );
        },
      });

      const deleted = response.data.deleteDriver.deleted;

      if (deleted) {
        message.success(
          {
            content: 'Cuenta eliminada correctamente',
            key: 'driver_delete',
          },
          4,
        );
      } else {
        message.error(
          {
            content: 'Ha habido un error',
            key: 'driver_delete',
          },
          4,
        );
      }
    } catch (e) {
      console.error(e);
      message.error(
        {
          content: 'Ha habido un error',
          key: 'driver_delete',
        },
        4,
      );
    } finally {
      setTimeout(() => {
        message.destroy();
      }, 3000);
    }
    this.props.hideDrawer();
  };

  render() {
    let content = <Skeleton active={true} loading={true} />;
    if (this.props.driverId && this.state.driver) {
      content = (
        <MyDriverUpdateForm
          onClickDelete={this.onDelete}
          onSubmit={this.props.onSubmit}
          driver={this.state.driver as unknown as Driver}
          drivingCompanies={this.state.drivingCompanies}
        />
      );
    }

    return (
      <Modal
        footer={[
          <Popconfirm title={'Estás seguro?'} onConfirm={this.onDelete}>
            <Button style={{ float: 'left' }} danger>
              Eliminar conductor
            </Button>
          </Popconfirm>,
          <Button
            onClick={async () => {
              const url = await getMobileApk('driver');
              const email =
                this.state.driver.volcanoEmail || this.state.driver.email;
              sendEmail(
                `mailto:${email}?subject=Aplicación móvil&body=${encodeURIComponent(
                  url as string,
                )}`,
              );
            }}
          >
            Enviar aplicación móvil
          </Button>,
          <Button
            form="update-driver-form"
            key="submit"
            htmlType="submit"
            type={'primary'}
          >
            Guardar cambios
          </Button>,
        ]}
        width={650}
        centered={true}
        closable={true}
        onCancel={this.props.onClose}
        destroyOnClose={true}
        visible={this.props.visible}
        title={'Gestionar conductor colaborador'}
      >
        {content}
      </Modal>
    );
  }
}
